<template>
	<Transition
		enter-active-class="transition-opacity duration-300 ease-out"
		enter-from-class="opacity-0"
		enter-to-class="opacity-100"
		leave-active-class="transition-opacity duration-200 ease-in"
		leave-from-class="opacity-100"
		leave-to-class="opacity-0"
	>
		<section
			v-if="isVisible"
			class="fixed inset-0 z-3002 flex items-end justify-center sm:items-center"
		>
			<div
				class="absolute inset-0 bg-black/60 backdrop-blur-sm"
				@click="toggleShareModal"
				@wheel.prevent
				@touchmove.prevent
				@scroll.prevent
			></div>

			<Transition
				enter-active-class="transition-all duration-400 ease-out"
				enter-from-class="opacity-0 translate-y-full sm:scale-95 sm:translate-y-8"
				enter-to-class="opacity-100 translate-y-0 sm:scale-100"
				leave-active-class="transition-all duration-250 ease-in"
				leave-from-class="opacity-100 translate-y-0 sm:scale-100"
				leave-to-class="opacity-0 translate-y-full sm:scale-95 sm:translate-y-4"
				appear
			>
				<form
					v-if="isVisible"
					method="dialog"
					class="z-10 w-full md:w-11/12 max-w-2xl bg-white py-8 px-7 rounded-xl relative shadow-2xl transform-gpu transition-all duration-300 ease-out "
					:style="{ height: modalHeight + 'px' }"
				>
					<!-- Close Button -->
					<button
						@click="toggleShareModal"
						type="button"
						class="p-2 hover:bg-slate-50 rounded-xl transition-colors duration-150 absolute top-2 right-2 cursor-pointer"
					>
						<XMarkIcon
							class="w-6 h-6 text-slate-500 hover:text-slate-700 transition-colors duration-150"
						/>
					</button>

					<fieldset>
						<legend
							class="mb-8 text-2xl capitalize text-slate-950 font-bold text-center"
						>
							<ShareIcon class="w-6 h-6 inline-block mr-2 mb-1" />
							{{ getTranslations("share") }}
						</legend>

						<!-- Tab Navigation -->
						<div class="flex bg-slate-100 rounded-lg p-1 mb-6 **:capitalize">
							<button
								@click="active_tab = 'link'"
								type="button"
								:class="[
									'cursor-pointer flex-1 flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md transition-colors duration-150',
									active_tab === 'link'
										? 'bg-white text-slate-900 shadow-sm'
										: 'text-slate-600 hover:text-slate-900 hover:bg-slate-50',
								]"
							>
								<LinkIcon class="w-4 h-4 mr-2" />
								{{ getTranslations("link") }}
							</button>
							<button
								@click="active_tab = 'embed'"
								type="button"
								:class="[
									'cursor-pointer flex-1 flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md transition-colors duration-150',
									active_tab === 'embed'
										? 'bg-white text-slate-900 shadow-sm'
										: 'text-slate-600 hover:text-slate-900 hover:bg-slate-50',
								]"
							>
								<CodeBracketIcon class="w-4 h-4 mr-2" />
								{{ getTranslations("embed") }}
							</button>
						</div>

						<!-- Tab Content Container with Fixed Height -->
						<div class="relative" :style="{ height: contentHeight + 'px' }">
							<!-- Link Sharing Tab -->
							<Transition
								enter-active-class="transition-all duration-300 ease-out"
								enter-from-class="opacity-0 translate-x-4"
								enter-to-class="opacity-100 translate-x-0"
								leave-active-class="transition-all duration-200 ease-in"
								leave-from-class="opacity-100 translate-x-0"
								leave-to-class="opacity-0 -translate-x-4"
								mode="out-in"
							>
								<div
									v-if="active_tab === 'link'"
									key="link"
									class="absolute inset-0 space-y-4"
								>
									<div class="space-y-2">
										<label class="block text-sm font-medium text-slate-700 capitalize">
											{{ getTranslations("share link") }}
										</label>
										<div
											class="flex rounded-lg border border-slate-300 overflow-hidden"
										>
											<input
												:value="shareUrl"
												readonly
												class="flex-1 px-3 py-2 text-sm text-slate-600 bg-slate-50 border-0 focus:outline-none"
												@focus="$event.target.select()"
											/>
											<button
												@click="copyToClipboard(shareUrl, 'link')"
												type="button"
												:class="[
													'cursor-pointer px-4 py-2 text-sm font-medium transition-colors duration-150 flex items-center space-x-2',
													link_copied
														? 'bg-green-50 text-green-700 border-green-200'
														: 'bg-slate-50 text-slate-700 hover:bg-slate-100 border-slate-300',
												]"
											>
												<CheckIcon v-if="link_copied" class="w-4 h-4" />
												<ClipboardDocumentIcon v-else class="w-4 h-4" />
												<span class="capitalize">{{
													link_copied
														? getTranslations("copied!")
														: getTranslations("copy")
												}}</span>
											</button>
										</div>
									</div>

									<!-- Native Share Button (if supported) -->
									<button
										v-if="isNativeShareSupported"
										@click="shareNative"
										type="button"
										class="w-full px-4 py-3 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors duration-150 flex items-center justify-center space-x-2 capitalize"
									>
										<ShareIcon class="w-4 h-4" />
										<span>{{ getTranslations("share") }}</span>
									</button>
								</div>

								<!-- Embed Code Tab -->
								<div
									v-else-if="active_tab === 'embed'"
									key="embed"
									class="absolute inset-0 space-y-4 overflow-y-auto"
								>
									<!-- Embed Type Selection -->
									<div class="space-y-2">
										<label class="block text-sm font-medium text-slate-700 capitalize">
											{{ getTranslations("embed type") }}
										</label>
										<div class="flex bg-slate-100 rounded-lg p-1">
											<button
												@click="embed_type = 'responsive'"
												type="button"
												:class="[
													'cursor-pointer flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 capitalize',
													embed_type === 'responsive'
														? 'bg-white text-slate-900 shadow-sm'
														: 'text-slate-600 hover:text-slate-900 hover:bg-slate-50',
												]"
											>
												{{ getTranslations("responsive") }}
											</button>
											<button
												@click="embed_type = 'fixed'"
												type="button"
												:class="[
													'cursor-pointer flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 capitalize',
													embed_type === 'fixed'
														? 'bg-white text-slate-900 shadow-sm'
														: 'text-slate-600 hover:text-slate-900 hover:bg-slate-50',
												]"
											>
												{{ getTranslations("fixed size") }}
											</button>
										</div>
									</div>

									<!-- Responsive Options -->
									<Transition
										enter-active-class="transition-all duration-250 ease-out"
										enter-from-class="opacity-0 scale-95"
										enter-to-class="opacity-100 scale-100"
										leave-active-class="transition-all duration-200 ease-in"
										leave-from-class="opacity-100 scale-100"
										leave-to-class="opacity-0 scale-95"
										mode="out-in"
									>
										<div
											v-if="embed_type === 'responsive'"
											key="responsive"
											class="space-y-2"
										>
											<label class="block text-sm font-medium text-slate-700 capitalize">
												{{ getTranslations("aspect ratio") }}
											</label>
											<div class="flex flex-wrap gap-2">
												<button
													v-for="ratio in aspect_ratios"
													:key="ratio.name"
													@click="selected_aspect_ratio = ratio"
													type="button"
													:class="[
														'cursor-pointer px-3 py-1 text-xs font-medium rounded-lg transition-colors duration-150',
														selected_aspect_ratio.name === ratio.name
															? 'bg-blue-100 text-blue-700 border border-blue-300'
															: 'text-slate-600 bg-slate-100 hover:bg-slate-200',
													]"
												>
													{{ ratio.name }}
												</button>
											</div>
										</div>

										<!-- Fixed Size Options -->
										<div v-else key="fixed" class="space-y-2">
											<label class="block text-sm font-medium text-slate-700 capitalize">
												{{ getTranslations("iframe size") }}
											</label>
											<div class="grid grid-cols-2 gap-3">
												<div>
													<label class="block text-xs text-slate-600 mb-1 capitalize">{{
														getTranslations("width")
													}}</label>
													<input
														v-model="iframe_width"
														type="number"
														min="200"
														max="1920"
														class="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
														placeholder="800"
													/>
												</div>
												<div>
													<label class="block text-xs text-slate-600 mb-1 capitalize">{{
														getTranslations("height")
													}}</label>
													<input
														v-model="iframe_height"
														type="number"
														min="200"
														max="1080"
														class="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
														placeholder="600"
													/>
												</div>
											</div>
											<div class="flex flex-wrap gap-2 mt-2">
												<button
													v-for="preset in size_presets"
													:key="`${preset.width}x${preset.height}`"
													@click="setPresetSize(preset)"
													type="button"
													class="px-3 py-1 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors duration-150"
												>
													{{ preset.name }}
												</button>
											</div>
										</div>
									</Transition>

									<div class="space-y-2">
										<label class="block text-sm font-medium text-slate-700 capitalize">
											{{ getTranslations("embed code") }}
										</label>
										<div class="relative">
											<textarea
												:value="embedCode"
												readonly
												rows="4"
												class="w-full px-3 py-2 text-xs text-slate-600 bg-slate-50 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono resize-none"
												@focus="$event.target.select()"
											></textarea>
											<button
												@click="copyToClipboard(embedCode, 'embed')"
												type="button"
												:class="[
													'cursor-pointer absolute top-2 right-2 px-3 py-1 text-xs font-medium rounded transition-colors duration-150 flex items-center space-x-1',
													embed_copied
														? 'bg-green-100 text-green-700'
														: 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-300 shadow-sm',
												]"
											>
												<CheckIcon v-if="embed_copied" class="w-3 h-3" />
												<ClipboardDocumentIcon v-else class="w-3 h-3" />
												<span>{{
													embed_copied
														? getTranslations("copied!")
														: getTranslations("copy")
												}}</span>
											</button>
										</div>
									</div>

									<div class="bg-blue-50 p-4 rounded-lg">
										<h4 class="text-sm font-medium text-blue-900 mb-2">
											{{
												getTranslations("Embed Instructions") || "How to embed:"
											}}
										</h4>
										<div class="text-sm text-blue-700 space-y-2">
											<p>
												{{ getTranslations("1. Copy the embed code above") }}
											</p>
											<p>
												{{
													getTranslations(
														"2. Paste it into your website's HTML where you want the content to appear",
													)
												}}
											</p>
											<p v-if="embed_type === 'responsive'">
												{{
													getTranslations(
														"3. The iframe will automatically adapt to different screen sizes while maintaining the selected aspect ratio",
													)
												}}
											</p>
											<p v-else>
												{{
													getTranslations(
														"3. The iframe will display with the exact dimensions specified",
													)
												}}
											</p>
											<p class="text-xs mt-2 text-blue-600">
												<span v-if="embed_type === 'responsive'">{{
													getTranslations(
														"Note: Responsive embeds work best for modern websites and provide optimal viewing across all devices.",
													)
												}}</span>
												<span v-else>{{
													getTranslations(
														"Note: Fixed size embeds are ideal when you need precise control over dimensions.",
													)
												}}</span>
											</p>
										</div>
									</div>
								</div>
							</Transition>
						</div>
					</fieldset>
				</form>
			</Transition>
		</section>
	</Transition>
</template>

<script lang="js" src="./ShareModal.js"></script>
